import Phaser from 'phaser';
import { DPR, POPUP_DEPTH } from '@constants/common.const';
import { ShadowNineSlice } from '../Shadow9Slice';
import { ModalSheetGeometry } from './ModalSheetGeometry';
import type { ModalSheetConfig, ModalSheetState } from './modal-sheet.types';
import { GameConfigService } from '@services/core/GameConfigService';

export class ModalSheetRenderer<TemplateData> {
  private drawerIconKey: string | undefined;

  constructor(
    private scene: Phaser.Scene,
    private geometry: ModalSheetGeometry<TemplateData>,
    private cfg: Required<ModalSheetConfig<TemplateData>>,
    private sheetBg: Phaser.GameObjects.RenderTexture,
    private grabber: Phaser.GameObjects.RenderTexture,
    private drawerIcon: Phaser.GameObjects.Container,
  ) {}

  public redraw(state: ModalSheetState): void {
    this.drawBackground(state);
    this.drawGrabber();
    this.drawDrawerIcon(state);
  }

  private drawBackground(state: ModalSheetState): void {
    const w = this.geometry.sheetWidth;
    const h = this.geometry.expandedHeight;
    this.sheetBg.setPosition(0, 0);
    this.sheetBg.resize(w, h);
    this.sheetBg.clear();

    const gfx = this.scene.add.graphics();
    gfx.fillStyle(this.cfg.backgroundColor, this.cfg.backgroundAlpha);
    this.drawRoundedRect(gfx, 0, 0, w, h, this.cfg.cornerRadius, false, state);
    this.sheetBg.draw(gfx, 0, 0);
    gfx.destroy();
  }

  private drawDrawerIcon(state: ModalSheetState): void {
    this.drawerIcon.setAlpha(state === 'expanded' ? 0 : 1);

    // Only rebuild contents when iconKey changes (or on first call)
    if (this.drawerIconKey === this.cfg.iconKey) return;
    this.drawerIconKey = this.cfg.iconKey;

    this.drawerIcon.removeAll(true);

    const iconSize = 48 * DPR;
    const bgSize = 64 * DPR;
    const radius = 16 * DPR;

    this.drawerIcon.setPosition(this.geometry.padding.left, -bgSize / 2);
    this.drawerIcon.setDepth(POPUP_DEPTH + 100);

    // Bake white rounded bg into RT — no earcut per frame
    const bgRT = this.scene.add.renderTexture(0, 0, bgSize, bgSize);
    bgRT.setOrigin(0, 0);
    const gfx = this.scene.add.graphics();
    gfx.fillStyle(new GameConfigService().getColor('--color-base-1')!, 1);
    this.drawRoundedRect(gfx, 0, 0, bgSize, bgSize, radius, true);
    bgRT.draw(gfx, 0, 0);
    gfx.destroy();

    const shadow = new ShadowNineSlice(
      this.scene,
      bgSize / 2,
      bgSize / 2,
      bgSize,
      bgSize,
      'rounded',
    );

    const icon = this.scene.add
      .image(bgSize / 2, bgSize / 2, this.cfg.iconKey)
      .setDisplaySize(iconSize, iconSize)
      .setOrigin(0.5);

    this.drawerIcon.add([shadow, bgRT, icon]);
  }

  private drawGrabber(): void {
    const gx = (this.geometry.sheetWidth - this.cfg.grabberWidth) / 2;
    const gy = 12 * DPR;
    this.grabber.setPosition(gx, gy);
    this.grabber.resize(this.cfg.grabberWidth, this.cfg.grabberHeight);
    this.grabber.clear();

    const gfx = this.scene.add.graphics();
    gfx.fillStyle(this.cfg.grabberColor, this.cfg.grabberAlpha);
    this.drawRoundedRect(
      gfx,
      0,
      0,
      this.cfg.grabberWidth,
      this.cfg.grabberHeight,
      this.cfg.grabberRadius,
      true,
      'collapsed',
    );
    this.grabber.draw(gfx, 0, 0);
    gfx.destroy();
  }

  private drawRoundedRect(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    isGrabber: boolean,
    state?: ModalSheetState,
  ): void {
    const defaultRadius = Math.min(radius, width / 2, height / 2);
    const rTop =
      isGrabber || state === 'collapsed' || state === 'hidden'
        ? defaultRadius
        : 0;
    const rBottom = isGrabber ? defaultRadius : 0;

    g.fillRoundedRect(x, y, width, height, {
      tl: rTop,
      tr: rTop,
      bl: rBottom,
      br: rBottom,
    });
  }
}
